import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownLeft,
  ArrowLeft,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  Download,
  Filter,
  Loader2,
  Search,
  Users,
  X,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AccountContextPanel from './AccountContextPanel';
import {
  buildTimeSeries,
  buildTransactionExportFilename,
  buildTypeDonutData,
  computeExtendedKpis,
  exportTransactionsCsv,
  filterTransactions,
  getDatePresetRange,
  getTopCounterparties,
  groupTransactionsByDate,
  sortTransactions,
  type DatePreset,
  type SortDir,
  type SortField,
  type TimeSeriesPoint,
} from './analyticsUtils';
import {
  formatShortDate,
  formatUsd,
  getTransactionAmount,
  type AccountContext,
  type TransactionRow,
  type TransactionSummary,
} from './types';

export interface TransactionInsightsViewProps {
  accountId: string;
  accountContext?: AccountContext | null;
  transactions: TransactionRow[];
  summary: TransactionSummary | null;
  onDateRangeChange?: (range: { since?: string; until?: string }) => void;
  dateRangeRequestError?: string | null;
  onBack: () => void;
}

type DirectionFilter = 'all' | 'Credit' | 'Debit';
type TimeSeriesChartType = 'line' | 'bar';

const CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 0 };

function TimeSeriesVolumeChart({
  data,
  chartType,
}: {
  data: TimeSeriesPoint[];
  chartType: TimeSeriesChartType;
}) {
  const axis = (
    <>
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ececec" />
      <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
      <YAxis
        tick={{ fontSize: 12, fill: '#6b7280' }}
        axisLine={false}
        tickLine={false}
        tickFormatter={(value) => `$${Number(value).toLocaleString()}`}
      />
      <Tooltip
        formatter={(value) => formatUsd(typeof value === 'number' ? value : 0)}
        contentStyle={{ borderRadius: 12, borderColor: '#e5e7eb', fontSize: 12 }}
      />
    </>
  );

  if (chartType === 'bar') {
    return (
      <BarChart data={data} margin={CHART_MARGIN}>
        {axis}
        <Bar dataKey="credits" name="Credits" fill="#10b981" radius={[6, 6, 0, 0]} maxBarSize={48} />
        <Bar dataKey="debits" name="Debits" fill="#f59e0b" radius={[6, 6, 0, 0]} maxBarSize={48} />
      </BarChart>
    );
  }

  return (
    <LineChart data={data} margin={CHART_MARGIN}>
      {axis}
      <Line type="monotone" dataKey="credits" name="Credits" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
      <Line type="monotone" dataKey="debits" name="Debits" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
    </LineChart>
  );
}

function Sparkline({ data }: { data: number[] }) {
  if (data.length === 0) {
    return <div className="h-8 mt-2" />;
  }

  const chartData = data.map((value, index) => ({ index, value }));

  return (
    <div className="h-8 mt-2">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <Line type="monotone" dataKey="value" stroke="#6b7280" strokeWidth={1.5} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function KpiCard({
  label,
  value,
  note,
  tone = 'neutral',
  sparkline,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'positive' | 'negative' | 'neutral';
  sparkline?: number[];
}) {
  const toneClass =
    tone === 'positive' ? 'text-emerald-600' : tone === 'negative' ? 'text-amber-600' : 'text-gray-900';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 h-full">
      <p className="text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">{label}</p>
      <p className={`text-xl sm:text-2xl font-semibold font-family-messina-sans mt-2 tabular-nums ${toneClass}`}>{value}</p>
      {note ? <p className="text-xs text-gray-500 font-family-messina-sans mt-1">{note}</p> : null}
      {sparkline ? <Sparkline data={sparkline} /> : null}
    </div>
  );
}

const DATE_PRESETS: Array<{ id: DatePreset; label: string }> = [
  { id: 'all', label: 'All' },
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
];

function dateInputBoundaryToIso(value: string, endOfDate: boolean): string | undefined {
  const parts = value.split('-').map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return undefined;
  const [year, month, day] = parts;
  const date = new Date(year, month - 1, day, endOfDate ? 23 : 0, endOfDate ? 59 : 0, endOfDate ? 59 : 0, endOfDate ? 999 : 0);
  return date.toISOString();
}

const TransactionInsightsView: React.FC<TransactionInsightsViewProps> = ({
  accountId,
  accountContext,
  transactions,
  summary,
  onDateRangeChange,
  dateRangeRequestError,
  onBack,
}) => {
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionRow | null>(null);
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('all');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [groupByDate, setGroupByDate] = useState(true);
  const [timeSeriesChartType, setTimeSeriesChartType] = useState<TimeSeriesChartType>('line');
  const [isExporting, setIsExporting] = useState(false);

  const typeOptions = useMemo(() => {
    const types = new Set<string>();
    transactions.forEach((tx) => {
      if (tx.type) types.add(tx.type);
    });
    return Array.from(types).sort();
  }, [transactions]);

  const presetRange = useMemo(() => getDatePresetRange(datePreset), [datePreset]);
  const dateRangeError = useMemo(() => {
    if (datePreset !== 'all' || !dateFrom || !dateTo) return null;
    const since = dateInputBoundaryToIso(dateFrom, false);
    const until = dateInputBoundaryToIso(dateTo, true);
    if (since && until && since > until) {
      return 'From date must be earlier than or equal to To date';
    }
    return null;
  }, [dateFrom, datePreset, dateTo]);

  useEffect(() => {
    if (!onDateRangeChange || dateRangeError) return;

    if (datePreset !== 'all') {
      onDateRangeChange({
        since: presetRange.from?.toISOString(),
        until: presetRange.to?.toISOString(),
      });
      return;
    }

    onDateRangeChange({
      since: dateFrom ? dateInputBoundaryToIso(dateFrom, false) : undefined,
      until: dateTo ? dateInputBoundaryToIso(dateTo, true) : undefined,
    });
  }, [dateFrom, datePreset, dateRangeError, dateTo, onDateRangeChange, presetRange.from, presetRange.to]);

  const filteredTransactions = useMemo(() => {
    const filtered = filterTransactions(transactions, {
      direction: directionFilter,
      types: selectedTypes.length > 0 ? selectedTypes : undefined,
      dateFrom: datePreset === 'all' && !dateRangeError ? dateFrom || undefined : undefined,
      dateTo: datePreset === 'all' && !dateRangeError ? dateTo || undefined : undefined,
      dateFromDate: datePreset !== 'all' ? presetRange.from : undefined,
      dateToDate: datePreset !== 'all' ? presetRange.to : undefined,
      search: searchQuery,
      amountMin: amountMin ? Number(amountMin) : null,
      amountMax: amountMax ? Number(amountMax) : null,
    });
    return sortTransactions(filtered, sortField, sortDir);
  }, [
    transactions,
    directionFilter,
    selectedTypes,
    datePreset,
    dateFrom,
    dateTo,
    dateRangeError,
    presetRange,
    searchQuery,
    amountMin,
    amountMax,
    sortField,
    sortDir,
  ]);

  const extendedKpis = useMemo(() => computeExtendedKpis(filteredTransactions), [filteredTransactions]);
  const filteredOverview = useMemo(() => {
    let totalCredits = 0;
    let totalDebits = 0;
    let lastTransactionDate: string | null = null;

    filteredTransactions.forEach((transaction) => {
      const amount = getTransactionAmount(transaction);
      if (transaction.direction === 'Credit') totalCredits += amount;
      if (transaction.direction === 'Debit') totalDebits += amount;
      if (transaction.createdAt && (!lastTransactionDate || transaction.createdAt > lastTransactionDate)) {
        lastTransactionDate = transaction.createdAt;
      }
    });

    return {
      netChange: totalCredits - totalDebits,
      totalCredits,
      totalDebits,
      totalTransactions: filteredTransactions.length,
      lastTransactionDate,
    };
  }, [filteredTransactions]);
  const timeSeries = useMemo(() => buildTimeSeries(filteredTransactions), [filteredTransactions]);
  const donutData = useMemo(() => buildTypeDonutData(filteredTransactions), [filteredTransactions]);
  const topCounterparties = useMemo(() => getTopCounterparties(filteredTransactions), [filteredTransactions]);
  const groupedRows = useMemo(() => groupTransactionsByDate(filteredTransactions), [filteredTransactions]);

  const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / pageSize));
  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTransactions.slice(start, start + pageSize);
  }, [filteredTransactions, currentPage, pageSize]);

  const paginatedGroupedRows = useMemo(() => {
    if (!groupByDate) return null;
    const start = (currentPage - 1) * pageSize;
    let count = 0;
    const result: typeof groupedRows = [];

    for (const group of groupedRows) {
      if (count >= start + pageSize) break;
      const remaining = pageSize - count;
      if (count + group.items.length <= start) {
        count += group.items.length;
        continue;
      }
      const sliceStart = Math.max(0, start - count);
      const items = group.items.slice(sliceStart, sliceStart + remaining);
      if (items.length > 0) {
        result.push({ ...group, items });
        count += items.length;
      }
    }
    return result;
  }, [groupedRows, groupByDate, currentPage, pageSize]);

  const hasActiveFilters =
    directionFilter !== 'all'
    || selectedTypes.length > 0
    || datePreset !== 'all'
    || dateFrom
    || dateTo
    || searchQuery
    || amountMin
    || amountMax;

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setCurrentPage(1);
  };

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]
    );
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDirectionFilter('all');
    setSelectedTypes([]);
    setDatePreset('all');
    setDateFrom('');
    setDateTo('');
    setSearchQuery('');
    setAmountMin('');
    setAmountMax('');
    setCurrentPage(1);
  };

  const handleExportCsv = async () => {
    if (isExporting || filteredTransactions.length === 0) return;

    setIsExporting(true);
    try {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });
      exportTransactionsCsv(
        filteredTransactions,
        buildTransactionExportFilename({
          artistName: accountContext?.artistName,
          accountId,
        })
      );
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 500);
      });
    } finally {
      setIsExporting(false);
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const renderTransactionRow = (tx: TransactionRow) => (
    <tr
      key={String(tx.id ?? `${tx.summary}-${tx.createdAt}`)}
      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => setSelectedTransaction(tx)}
    >
      <td className="py-3 px-4 text-sm text-gray-900 font-family-messina-sans max-w-[280px] truncate">
        {tx.summary || '—'}
      </td>
      <td className="py-3 px-4 text-sm text-gray-700 font-family-messina-sans whitespace-nowrap">
        {formatShortDate(tx.createdAt)}
      </td>
      <td className="py-3 px-4 text-sm text-gray-700 font-family-messina-sans">{tx.type || '—'}</td>
      <td className="py-3 px-4">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            tx.direction === 'Credit' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
          }`}
        >
          {tx.direction === 'Credit' ? <ArrowDownLeft size={12} /> : <ArrowUpRight size={12} />}
          {tx.direction || '—'}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-gray-900 font-family-messina-sans text-right tabular-nums">
        {formatUsd(getTransactionAmount(tx))}
      </td>
    </tr>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 bg-[#F8F7F2] min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={onBack}
              className="mb-3 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors font-family-messina-sans"
            >
              <ArrowLeft size={16} />
              Back
            </button>
            <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">
              Transaction insights
            </span>
          </div>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={isExporting || filteredTransactions.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 font-family-messina-sans disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isExporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>

        <AccountContextPanel accountContext={accountContext} />

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          <KpiCard label="Avg transaction" value={formatUsd(extendedKpis.avgSize)} sparkline={extendedKpis.dailyNetSparkline} />
          <KpiCard label="Largest credit" value={formatUsd(extendedKpis.largestCredit)} tone="positive" />
          <KpiCard label="Largest debit" value={formatUsd(extendedKpis.largestDebit)} tone="negative" />
          <KpiCard
            label="Fee total"
            value={formatUsd(extendedKpis.feeTotal)}
            note={extendedKpis.mostActiveDay ? `Most active day: ${extendedKpis.mostActiveDay}` : undefined}
            tone={extendedKpis.feeTotal > 0 ? 'negative' : 'neutral'}
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 items-stretch">
          <div className="xl:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 flex flex-col h-full min-h-[22rem]">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <h3 className="text-sm font-medium text-gray-900 font-family-messina-sans">Credits vs debits over time</h3>
                <p className="text-xs text-gray-500 font-family-messina-sans mt-1">Daily volume from filtered transactions</p>
              </div>
              <select
                value={timeSeriesChartType}
                onChange={(e) => setTimeSeriesChartType(e.target.value as TimeSeriesChartType)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 font-family-messina-sans bg-white shrink-0"
                aria-label="Chart type"
              >
                <option value="line">Line chart</option>
                <option value="bar">Bar chart</option>
              </select>
            </div>
            <div className="flex-1 mt-4 min-h-0 flex flex-col">
              {timeSeries.length > 0 ? (
                <div className="flex-1 min-h-0 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <TimeSeriesVolumeChart data={timeSeries} chartType={timeSeriesChartType} />
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex-1 min-h-[14rem] w-full flex items-center justify-center text-sm text-gray-500 font-family-messina-sans">
                  No time-series data
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4 h-full">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5">
              <h3 className="text-sm font-medium text-gray-900 font-family-messina-sans">Volume by type</h3>
              <div className="h-48 mt-4">
                {donutData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                        {donutData.map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatUsd(typeof value === 'number' ? value : 0)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-gray-500 font-family-messina-sans">
                    No type data
                  </div>
                )}
              </div>
              <div className="space-y-2 mt-2">
                {donutData.slice(0, 4).map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-xs font-family-messina-sans">
                    <span className="flex items-center gap-2 text-gray-700">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.fill }} />
                      {item.name}
                    </span>
                    <span className="text-gray-900 tabular-nums">{formatUsd(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 flex-1 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Users size={16} className="text-gray-600" />
                <h3 className="text-sm font-medium text-gray-900 font-family-messina-sans">Top counterparties</h3>
              </div>
              <div className="space-y-3 flex-1">
                {topCounterparties.length === 0 ? (
                  <p className="text-sm text-gray-500 font-family-messina-sans">No counterparty data</p>
                ) : (
                  topCounterparties.map((item) => (
                    <div key={item.name} className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900 font-family-messina-sans truncate">{item.name}</p>
                        <p className="text-xs text-gray-500 font-family-messina-sans">{item.count} transaction(s) · {item.direction}</p>
                      </div>
                      <span className="text-sm text-gray-900 tabular-nums shrink-0 font-family-messina-sans">{formatUsd(item.total)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {summary ? (
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard
              label="Net change"
              value={`${filteredOverview.netChange >= 0 ? '+' : ''}${formatUsd(filteredOverview.netChange)}`}
              tone={filteredOverview.netChange >= 0 ? 'positive' : 'negative'}
              sparkline={extendedKpis.dailyNetSparkline}
            />
            <KpiCard label="Total credits" value={formatUsd(filteredOverview.totalCredits)} tone="positive" />
            <KpiCard label="Total debits" value={formatUsd(filteredOverview.totalDebits)} tone="negative" />
            <KpiCard
              label="All transactions"
              value={String(filteredOverview.totalTransactions)}
              note={filteredOverview.lastTransactionDate ? `Last activity ${formatShortDate(filteredOverview.lastTransactionDate)}` : undefined}
            />
          </div>
        ) : null}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-200 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="text-sm font-medium text-gray-900 font-family-messina-sans">Transactions</h3>
                <p className="text-xs text-gray-500 font-family-messina-sans mt-1">
                  {hasActiveFilters
                    ? `Showing ${filteredTransactions.length} of ${transactions.length}`
                    : `${transactions.length} total`}
                </p>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 font-family-messina-sans">
                <input
                  type="checkbox"
                  checked={groupByDate}
                  onChange={(e) => {
                    setGroupByDate(e.target.checked);
                    setCurrentPage(1);
                  }}
                  className="rounded border-gray-300"
                />
                Group by date
              </label>
            </div>

            <div className="flex flex-wrap gap-2">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => {
                    setDatePreset(preset.id);
                    setDateFrom('');
                    setDateTo('');
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium font-family-messina-sans border transition-colors ${
                    datePreset === preset.id
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search summary or counterparty"
                  className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm font-family-messina-sans"
                />
              </div>
              <select
                value={directionFilter}
                onChange={(e) => {
                  setDirectionFilter(e.target.value as DirectionFilter);
                  setCurrentPage(1);
                }}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 font-family-messina-sans bg-white"
              >
                <option value="all">All directions</option>
                <option value="Credit">Credits</option>
                <option value="Debit">Debits</option>
              </select>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amountMin}
                onChange={(e) => {
                  setAmountMin(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Min amount"
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-family-messina-sans"
              />
              <input
                type="number"
                min="0"
                step="0.01"
                value={amountMax}
                onChange={(e) => {
                  setAmountMax(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Max amount"
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-family-messina-sans"
              />
            </div>

            {datePreset === 'all' ? (
              <div>
                <div className="flex flex-wrap items-end gap-3">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-family-messina-sans bg-white"
                    aria-label="From date"
                  />
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-family-messina-sans bg-white"
                    aria-label="To date"
                  />
                </div>
                {dateRangeError || dateRangeRequestError ? (
                  <p className="mt-2 text-xs text-red-600 font-family-messina-sans" role="alert">
                    {dateRangeError || dateRangeRequestError}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500 font-family-messina-sans flex items-center gap-1">
                <Filter size={12} />
                Types
              </span>
              {typeOptions.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleType(type)}
                  className={`px-2.5 py-1 rounded-full text-xs font-family-messina-sans border ${
                    selectedTypes.includes(type)
                      ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                      : 'bg-white text-gray-600 border-gray-200'
                  }`}
                >
                  {type}
                </button>
              ))}
              {hasActiveFilters ? (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-2.5 py-1 text-xs text-gray-600 hover:text-gray-900 font-family-messina-sans"
                >
                  Clear all
                </button>
              ) : null}
            </div>
          </div>

          <div className="overflow-x-auto">
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-10 text-sm text-gray-600 font-family-messina-sans">
                {transactions.length === 0 ? 'No transactions found' : 'No transactions match the selected filters'}
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/70">
                    <th className="text-left py-3 px-4">
                      <button type="button" onClick={() => toggleSort('summary')} className="inline-flex items-center gap-1 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Summary <SortIcon field="summary" />
                      </button>
                    </th>
                    <th className="text-left py-3 px-4">
                      <button type="button" onClick={() => toggleSort('date')} className="inline-flex items-center gap-1 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Date <SortIcon field="date" />
                      </button>
                    </th>
                    <th className="text-left py-3 px-4">
                      <button type="button" onClick={() => toggleSort('type')} className="inline-flex items-center gap-1 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Type <SortIcon field="type" />
                      </button>
                    </th>
                    <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Direction</th>
                    <th className="text-right py-3 px-4">
                      <button type="button" onClick={() => toggleSort('amount')} className="inline-flex items-center gap-1 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item ml-auto">
                        Amount <SortIcon field="amount" />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groupByDate && paginatedGroupedRows
                    ? paginatedGroupedRows.map((group) => (
                      <React.Fragment key={group.dateKey}>
                        <tr className="bg-gray-50/80">
                          <td colSpan={5} className="py-2 px-4 text-xs font-medium text-gray-600 font-family-messina-sans">
                            {group.dateLabel} · {group.items.length} transaction(s)
                          </td>
                        </tr>
                        {group.items.map(renderTransactionRow)}
                      </React.Fragment>
                    ))
                    : paginatedTransactions.map(renderTransactionRow)}
                </tbody>
              </table>
            )}
          </div>

          {filteredTransactions.length > 0 ? (
            <div className="px-4 sm:px-5 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-gray-600 font-family-messina-sans">
                <span>Rows per page</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-gray-200 rounded px-2 py-1 text-sm bg-white"
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-50 font-family-messina-sans"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 font-family-messina-sans">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-50 font-family-messina-sans"
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {selectedTransaction ? (
        <div className="fixed inset-0 z-[9999] flex justify-end bg-black/40" onClick={() => setSelectedTransaction(null)}>
          <div
            className="h-full w-full max-w-md bg-white shadow-2xl border-l border-gray-200 p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-6">
              <div>
                <p className="text-[0.625rem] uppercase tracking-wide text-gray-500 sidebar-menu-item">Transaction</p>
                <h3 className="text-lg font-medium text-gray-900 font-family-messina-modern mt-1">
                  {selectedTransaction.summary || 'Transaction details'}
                </h3>
              </div>
              <button type="button" onClick={() => setSelectedTransaction(null)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 p-4">
                <p className="text-xs text-gray-500 font-family-messina-sans">Amount</p>
                <p className="text-2xl font-semibold text-gray-900 font-family-messina-sans mt-1 tabular-nums">
                  {formatUsd(getTransactionAmount(selectedTransaction))}
                </p>
              </div>
              {[
                ['Date', formatShortDate(selectedTransaction.createdAt)],
                ['Type', selectedTransaction.type || '—'],
                ['Direction', selectedTransaction.direction || '—'],
                ['Counterparty', selectedTransaction.name || '—'],
                ['Transaction ID', selectedTransaction.id != null ? String(selectedTransaction.id) : '—'],
              ].map(([label, value]) => (
                <div key={label} className="flex items-start justify-between gap-4 py-2 border-b border-gray-100">
                  <span className="text-xs uppercase tracking-wide text-gray-500 sidebar-menu-item">{label}</span>
                  <span className="text-sm text-gray-900 font-family-messina-sans text-right break-all">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TransactionInsightsView;
